import { t } from 'i18next';

import { getAppGlobals } from '~/components/AppGlobals/utils';
import useIsLoggedIn from '~/hooks/useIsLoggedIn';
import { usePush } from '~/hooks/usePush';
import {
  type CommunityPushOptInSurface,
  getCommunityPushContext,
  withCommunityPushLock,
} from '~/utils/communityAppAdoption';
import { NectarPageBlock } from '~/utils/nectarPageBlock';
import { registerPushSubscription, unregisterPushSubscription } from '~/utils/pushSubscription';
import toast from '~/utils/toast';

export function useEnablePushNotification(surface: CommunityPushOptInSurface = NectarPageBlock.CommunityAppPushPrompt) {
  const appGlobals = getAppGlobals();
  const isLoggedIn = useIsLoggedIn();
  const { subscribeToPush, unsubscribeFromPush, isSubscribed, canSendPush, setIsSubscribed } = usePush();

  // Resolves true only when push was actually enabled/disabled end-to-end, so
  // the prompt can avoid persisting its dismissal on failure.
  async function onEnable(): Promise<boolean> {
    const activeContext = getCommunityPushContext();
    const context = activeContext ? { ...activeContext, pageBlock: surface } : undefined;
    if (!isLoggedIn) {
      toast.error(t('nav.push.enable.error'));
      return false;
    }

    if (isSubscribed) {
      try {
        return await withCommunityPushLock(async () => {
          const subscription = await unsubscribeFromPush();
          if (!subscription) return false;
          const succeeded = await unregisterPushSubscription(subscription, context);
          succeeded ? toast.success(t('nav.push.disable.success')) : toast.error(t('nav.push.disable.error'));
          return succeeded;
        });
      } catch {
        toast.error(t('nav.push.disable.error'));
        return false;
      }
    }

    if (!canSendPush) {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        // Once permission is 'denied' the browser/OS won't re-prompt — the member
        // has to re-allow it in the installed app's settings. 'default' means the
        // native prompt was dismissed without choosing, which is retryable, so we
        // stay silent there.
        if (permission === 'denied') {
          toast.error(t('nav.push.enable.blocked'));
        }
        return false;
      }
    }

    try {
      return await withCommunityPushLock(async () => {
        const subscription = await subscribeToPush(appGlobals.VAPID_PUBLIC_KEY);
        if (!subscription) return false;
        const succeeded = await registerPushSubscription(subscription, context);
        setIsSubscribed(succeeded);
        succeeded ? toast.success(t('nav.push.enable.success')) : toast.error(t('nav.push.enable.failed'));
        return succeeded;
      });
    } catch {
      // Browser subscribe failed (e.g. invalid VAPID key) — not an auth problem.
      toast.error(t('nav.push.enable.failed'));
      return false;
    }
  }
  return { onEnable, isSubscribed };
}
